import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Card, CardContent } from '@mui/material';

import type {
  FilterableTableColumnType,
  FilterableTableOnSortChangeFunc,
  FilterableTablePaginationType,
  FilterableTableRowMapperFunc,
  FilterTableOnPaginationChangeFunc,
  KebabMenuItem,
} from 'app/components';
import { FilterableTable, HasAccessTo, KebabMenu } from 'app/components';
import { TranslationKeys } from 'app/translations';
import type { SummaryScales } from 'app/types';

import { SETTINGS } from 'app/common/Authorization/entities';
import { EDIT } from 'app/common/Authorization/permissions';
import { SettingsSummaryScalesCustomTag } from '../SettingsSummaryScalesCustomTag';

export const SETTINGS_SUMMARY_SCALE_DATA_TEST_ID = 'settings-summary-scale-component';

enum TableColumns {
  Code = 'code',
  Owner = 'owner',
  Product = 'product',
  Title = 'title',
  Year = 'year',
  TaxLimitPercentage = 'taxLimitPercentage',
  Actions = 'col_actions',
}

type SettingsSummaryScalesProps = {
  isLoading?: boolean;
  summaryScales: SummaryScales[];
  onDeleteActionClick: (summaryScaleId: SummaryScales['summaryOfRegulationScaleId']) => void;
  onDownloadActionClick: (summaryScale: SummaryScales) => void;
  pagination: FilterableTablePaginationType;
  onPaginationChange: FilterTableOnPaginationChangeFunc;
  onSortChange: FilterableTableOnSortChangeFunc;
};

export const SettingsSummaryScales = ({
  summaryScales,
  pagination,
  onDeleteActionClick,
  onDownloadActionClick,
  onPaginationChange,
  onSortChange,
  isLoading = false,
}: SettingsSummaryScalesProps) => {
  // const { locale } = useLocale();
  const hasPermissionToEdit = HasAccessTo(SETTINGS, EDIT);

  const tableHeaders = React.useMemo<FilterableTableColumnType[]>(() => {
    return [
      {
        name: TableColumns.Title,
        title: <FormattedMessage id={TranslationKeys.global_title} />,
      },
      {
        name: TableColumns.Year,
        title: <FormattedMessage id={TranslationKeys.global_year} />,
        sortable: true,
      },
      {
        name: TableColumns.TaxLimitPercentage,
        title: <FormattedMessage id={TranslationKeys.global_interestRateScale} />,
        sortable: true,
      },
      {
        name: TableColumns.Actions,
        isActionsColumn: true,
      },
    ];
  }, []);

  const buildRowActions = React.useCallback(
    (summaryScale: SummaryScales) => {
      const actions: Array<KebabMenuItem> = [
        {
          element: <FormattedMessage id={TranslationKeys.global_download} />,
          onClick: () => onDownloadActionClick(summaryScale),
        },
        {
          element: <FormattedMessage id={TranslationKeys.global_delete} />,
          onClick: () => onDeleteActionClick(summaryScale.summaryOfRegulationScaleId),
          disabled: !hasPermissionToEdit,
        },
      ];

      return <KebabMenu items={actions} />;
    },
    [hasPermissionToEdit, onDeleteActionClick, onDownloadActionClick],
  );

  const tableRowMapper = React.useCallback<FilterableTableRowMapperFunc<SummaryScales>>(
    summaryScale => {
      return {
        data: {
          [TableColumns.Title]: (
            <div>
              {summaryScale.title} {summaryScale.owner && <SettingsSummaryScalesCustomTag />}
            </div>
          ),
          [TableColumns.Year]: summaryScale.year,
          [TableColumns.TaxLimitPercentage]: summaryScale.taxLimitPercentage,
          [TableColumns.Actions]: buildRowActions(summaryScale),
        },
      };
    },
    [buildRowActions],
  );

  return (
    <Card data-testid={SETTINGS_SUMMARY_SCALE_DATA_TEST_ID} sx={{ borderRadius: '10px' }}>
      <CardContent sx={{ padding: 0 }}>
        <FilterableTable<SummaryScales>
          columns={tableHeaders}
          loading={isLoading}
          rows={summaryScales}
          rowMapper={tableRowMapper}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          onSortChange={onSortChange}
        />
      </CardContent>
    </Card>
  );
};
